import { Globe2, HandCoins, LoaderCircle, Network, PlusCircle, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";


const formatCurrency = (value) => `S$${Number(value || 0).toLocaleString("en-SG", { maximumFractionDigits: 0 })}`;

const fieldClass = "h-12 rounded-[18px] border border-[#001935]/12 bg-white px-4 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]";

export const AgentNetworkWorkspace = ({
  connectingAgentId,
  deals,
  dealForm,
  directoryAgents,
  directoryFilters,
  directoryLoading,
  earningsSummary,
  incomingRequests,
  onAcceptRequest,
  onChangeDealField,
  onChangeFilter,
  onConnect,
  onPostDeal,
  onRefreshDirectory,
  postingDeal,
  requestsBusyId,
  user,
}) => {
  const [activeSubTab, setActiveSubTab] = useState("directory");
  const [showDealModal, setShowDealModal] = useState(false);

  const directoryCountLabel = useMemo(() => `${directoryAgents.length} verified agents`, [directoryAgents.length]);

  return (
    <div className="grid gap-6 pb-28" data-testid="agent-network-workspace">
      <div className="rounded-[32px] border border-[#001935]/10 bg-[linear-gradient(135deg,#001935_0%,#042143_48%,#0b2f59_100%)] p-6 text-white shadow-[0_24px_70px_rgba(0,25,53,0.18)] md:p-8" data-testid="agent-network-hero">
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#F5D98C]">Professional Agent Network</p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-5">
          <div className="max-w-3xl">
            <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-white md:text-5xl" data-testid="agent-network-hero-heading">
              Find verified peers, co-broke faster, and keep deal referrals inside your private workspace.
            </h2>
            <p className="mt-4 text-sm leading-8 text-white/78" data-testid="agent-network-hero-copy">
              Search the verified network by specialty, district, and language, then publish anonymised leads into the B2B exchange while your referral streams stay consolidated in one ledger.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3" data-testid="agent-network-overview-cards">
            {[
              ["Connections", earningsSummary?.overview?.connections_count || 0, Users],
              ["Incoming Requests", earningsSummary?.overview?.incoming_requests_count || 0, Network],
              ["Active Exchange Leads", earningsSummary?.overview?.active_deals_count || 0, HandCoins],
            ].map(([label, value, Icon]) => (
              <div className="min-w-[10rem] rounded-[22px] border border-white/12 bg-white/8 px-4 py-4" data-testid={`agent-network-overview-${String(label).toLowerCase().replace(/\s+/g, "-")}`} key={label}>
                <div className="flex items-center gap-2 text-[#F5D98C]"><Icon className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.18em]">{label}</p></div>
                <p className="mt-3 text-3xl font-semibold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="agent-network-subtab-list">
        {[
          ["directory", "Agent Directory"],
          ["deals", "B2B Deal Exchange"],
          ["earnings", "Earnings & Referrals"],
        ].map(([key, label]) => (
          <button
            className={`rounded-full border px-5 py-3 text-sm font-semibold transition ${activeSubTab === key ? "border-[#D4AF37]/40 bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/30 hover:text-[#001935]"}`}
            data-testid={`agent-network-tab-${key}`}
            key={key}
            onClick={() => setActiveSubTab(key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      {activeSubTab === "directory" ? (
        <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]" data-testid="agent-network-directory-panel">
          <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Search Filters</p>
            <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Find the right co-broke partner</h3>
            <p className="mt-3 text-sm leading-7 text-[#42566f]">Search by district, specialty, and language, then send a direct connection request into the verified network.</p>

            <div className="mt-6 grid gap-3">
              <select className={fieldClass} data-testid="agent-network-district-filter" onChange={(event) => onChangeFilter("target_district", event.target.value)} value={directoryFilters.target_district}>
                <option value="">All Districts</option>
                {Array.from({ length: 28 }, (_, index) => `${index + 1}`.padStart(2, "0")).map((district) => <option key={district} value={district}>{district}</option>)}
              </select>
              <select className={fieldClass} data-testid="agent-network-specialty-filter" onChange={(event) => onChangeFilter("specialty", event.target.value)} value={directoryFilters.specialty}>
                <option value="">All Specialties</option>
                {["HDB", "Condo", "Commercial", "Industrial", "New Launch", "Luxury"].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className={fieldClass} data-testid="agent-network-language-filter" onChange={(event) => onChangeFilter("language", event.target.value)} value={directoryFilters.language}>
                <option value="">All Languages</option>
                {["English", "Mandarin", "Malay", "Tamil"].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <button className="gold-button !justify-center" data-testid="agent-network-refresh-directory-button" onClick={onRefreshDirectory} type="button">
                {directoryLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />} Refresh results
              </button>
            </div>

            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/20 bg-[#fffaf1] px-4 py-4" data-testid="agent-network-incoming-requests">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Pending Incoming Requests</p>
              <div className="mt-3 grid gap-3">
                {incomingRequests?.length ? incomingRequests.map((agent) => (
                  <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`agent-network-incoming-request-${agent.id}`} key={agent.id}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-[#001935]">{agent.name}</p>
                        <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#8b6d16]">{agent.agency}</p>
                      </div>
                      <button className="gold-button !px-4 !py-2 !text-xs" data-testid={`agent-network-accept-request-${agent.id}`} disabled={requestsBusyId === agent.id} onClick={() => onAcceptRequest(agent.id)} type="button">
                        {requestsBusyId === agent.id ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null} Accept
                      </button>
                    </div>
                  </div>
                )) : <p className="text-sm leading-7 text-[#6b6252]" data-testid="agent-network-incoming-request-empty">No pending requests right now.</p>}
              </div>
            </div>
          </div>

          <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Verified Directory</p>
                <h3 className="mt-2 text-3xl font-semibold text-[#001935]">{directoryCountLabel}</h3>
              </div>
              <div className="rounded-full border border-[#001935]/10 bg-[#f8f8f6] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]">Verified agents only</div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="agent-network-directory-grid">
              {directoryLoading ? <div className="col-span-full rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-5 py-10 text-center text-sm text-[#42566f]" data-testid="agent-network-directory-loading"><LoaderCircle className="mx-auto h-5 w-5 animate-spin text-[#8b6d16]" /><p className="mt-3">Refreshing the verified network…</p></div> : null}
              {!directoryLoading && directoryAgents.length ? directoryAgents.map((agent) => (
                <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`agent-network-directory-card-${agent.id}`} key={agent.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-lg font-semibold text-[#001935]" data-testid={`agent-network-agent-name-${agent.id}`}>{agent.name}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.14em] text-[#8b6d16]" data-testid={`agent-network-agent-agency-${agent.id}`}>{agent.agency}</p>
                      {agent.cea_registration_number ? <p className="mt-2 text-xs uppercase tracking-[0.14em] text-[#42566f]">CEA {agent.cea_registration_number}</p> : null}
                    </div>
                    <button
                      className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] ${agent.relationship_status === "connected" ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : agent.relationship_status === "pending" ? "border border-amber-200 bg-amber-50 text-amber-800" : "bg-[#001935] text-white"}`}
                      data-testid={`agent-network-connect-button-${agent.id}`}
                      disabled={agent.relationship_status !== "connect" || connectingAgentId === agent.id}
                      onClick={() => onConnect(agent.id)}
                      type="button"
                    >
                      {connectingAgentId === agent.id ? <LoaderCircle className="mr-2 inline h-3.5 w-3.5 animate-spin" /> : null}
                      {agent.relationship_status === "connected" ? "Connected" : agent.relationship_status === "pending" ? "Pending" : agent.relationship_status === "incoming" ? "Requested You" : "Connect"}
                    </button>
                  </div>

                  <div className="mt-5 grid gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Core Specialties</p>
                      <div className="mt-2 flex flex-wrap gap-2">{(agent.specialties || []).length ? agent.specialties.map((item) => <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-1 text-xs font-semibold text-[#42566f]" key={item}>{item}</span>) : <span className="text-sm text-[#6b6252]">No specialties listed yet.</span>}</div>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Target Districts</p>
                      <div className="mt-2 flex flex-wrap gap-2">{(agent.target_districts || []).length ? agent.target_districts.map((item) => <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-1 text-xs font-semibold text-[#42566f]" key={item}>D{item}</span>) : <span className="text-sm text-[#6b6252]">No district focus added yet.</span>}</div>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Languages</p>
                      <div className="mt-2 flex flex-wrap gap-2">{(agent.languages || []).length ? agent.languages.map((item) => <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-1 text-xs font-semibold text-[#42566f]" key={item}>{item}</span>) : <span className="text-sm text-[#6b6252]">Languages not listed.</span>}</div>
                    </div>
                  </div>
                </div>
              )) : null}
              {!directoryLoading && !directoryAgents.length ? <div className="col-span-full rounded-[24px] border border-dashed border-[#001935]/12 bg-white px-5 py-8 text-center text-sm leading-7 text-[#6b6252]" data-testid="agent-network-directory-empty">No verified agents matched those filters yet.</div> : null}
            </div>
          </div>
        </div>
      ) : null}

      {activeSubTab === "deals" ? (
        <div className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]" data-testid="agent-network-deals-panel">
          <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Marketplace Board</p>
            <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Broadcast anonymised co-broke opportunities</h3>
            <p className="mt-3 text-sm leading-7 text-[#42566f]">Post the broad deal criteria only. Client identities and sensitive personal details should stay outside the board.</p>
            <div className="mt-6 rounded-[24px] border border-[#D4AF37]/16 bg-[#fffaf1] px-4 py-4 text-sm leading-7 text-[#5f5a4e]" data-testid="agent-network-deal-board-note">
              Example: “Looking for $5M Tuas Warehouse | 20% Commission Split” or “Need a District 15 condo buyer match up to $2.2M”.
            </div>
            <button className="gold-button mt-6 !justify-center" data-testid="agent-network-open-post-lead-modal" onClick={() => setShowDealModal(true)} type="button">
              <PlusCircle className="mr-2 h-4 w-4" /> Post a Lead
            </button>
          </div>

          <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Active B2B Leads</p>
                <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Open exchange board</h3>
              </div>
              <div className="rounded-full border border-[#001935]/10 bg-[#f8f8f6] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]">{deals.length} active</div>
            </div>

            <div className="mt-6 grid gap-4" data-testid="agent-network-deals-grid">
              {deals.length ? deals.map((deal) => (
                <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`agent-network-deal-card-${deal.id}`} key={deal.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-[#001935]" data-testid={`agent-network-deal-headline-${deal.id}`}>{deal.headline}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.14em] text-[#8b6d16]">{deal.lead_type} • {deal.transaction_type} • District {deal.location_district}</p>
                    </div>
                    <div className="rounded-full border border-[#001935]/10 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#42566f]">{deal.status}</div>
                  </div>
                  <p className="mt-4 text-sm leading-7 text-[#42566f]" data-testid={`agent-network-deal-description-${deal.id}`}>{deal.description}</p>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-[0.14em] text-[#6b6252]">
                    <span>{deal.posted_by_me ? "Posted by you" : "Posted by another verified agent"}</span>
                    <span>{new Date(deal.created_at).toLocaleDateString("en-SG")}</span>
                  </div>
                </div>
              )) : <div className="rounded-[24px] border border-dashed border-[#001935]/12 bg-white px-5 py-8 text-center text-sm leading-7 text-[#6b6252]" data-testid="agent-network-deals-empty">No active deal referrals have been posted yet.</div>}
            </div>
          </div>
        </div>
      ) : null}

      {activeSubTab === "earnings" ? (
        <div className="grid gap-6" data-testid="agent-network-earnings-panel">
          <div className="grid gap-6 xl:grid-cols-2">
            <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
              <div className="flex items-center gap-2 text-[#8b6d16]"><Globe2 className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.18em]">Platform Referrals</p></div>
              <h3 className="mt-3 text-3xl font-semibold text-[#001935]">SaaS subscription commissions</h3>
              <p className="mt-3 text-sm leading-7 text-[#42566f]">{earningsSummary?.platform_referrals?.status_label || "No linked platform referral ledger yet."}</p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {[
                  ["Referred Agents", earningsSummary?.platform_referrals?.overview?.referred_agents || 0],
                  ["Revenue Generated", formatCurrency(earningsSummary?.platform_referrals?.overview?.monthly_revenue_generated || 0)],
                  ["Pending Payout", formatCurrency(earningsSummary?.platform_referrals?.overview?.pending_payout_total || 0)],
                ].map(([label, value]) => (
                  <div className="rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid={`agent-network-platform-referral-${String(label).toLowerCase().replace(/\s+/g, '-')}`} key={label}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8b6d16]">{label}</p>
                    <p className="mt-3 text-2xl font-semibold text-[#001935]">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
              <div className="flex items-center gap-2 text-[#8b6d16]"><HandCoins className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.18em]">Deal Referrals</p></div>
              <h3 className="mt-3 text-3xl font-semibold text-[#001935]">Co-broking revenue stream</h3>
              <p className="mt-3 text-sm leading-7 text-[#42566f]" data-testid="agent-network-deal-referral-mocked-note">{earningsSummary?.deal_referrals?.mocked_summary?.note}</p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {[
                  ["My Posted Leads", earningsSummary?.deal_referrals?.overview?.posted_by_me || 0],
                  ["Pipeline Earnings", formatCurrency(earningsSummary?.deal_referrals?.mocked_summary?.projected_pipeline_earnings || 0)],
                  ["Closed Earnings", formatCurrency(earningsSummary?.deal_referrals?.mocked_summary?.projected_closed_earnings || 0)],
                ].map(([label, value]) => (
                  <div className="rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid={`agent-network-deal-referral-${String(label).toLowerCase().replace(/\s+/g, '-')}`} key={label}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8b6d16]">{label}</p>
                    <p className="mt-3 text-2xl font-semibold text-[#001935]">{value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 inline-flex rounded-full border border-[#D4AF37]/25 bg-[#fff4db] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid="agent-network-deal-referral-mocked-pill">
                {earningsSummary?.deal_referrals?.mocked_summary?.settlement_status || "MOCKED"} summary until settlement ledger goes live
              </div>
            </div>
          </div>

          <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Recent Deal Referrals</p>
            <div className="mt-4 grid gap-3">
              {(earningsSummary?.deal_referrals?.recent_posted || []).length ? earningsSummary.deal_referrals.recent_posted.map((deal) => (
                <div className="rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid={`agent-network-recent-posted-deal-${deal.id}`} key={deal.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[#001935]">{deal.headline}</p>
                    <span className="text-xs uppercase tracking-[0.14em] text-[#8b6d16]">{deal.status}</span>
                  </div>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]">{deal.description}</p>
                </div>
              )) : <p className="text-sm leading-7 text-[#6b6252]" data-testid="agent-network-recent-posted-empty">No posted deal referrals yet.</p>}
            </div>
          </div>
        </div>
      ) : null}

      <Dialog onOpenChange={setShowDealModal} open={showDealModal}>
        <DialogContent className="max-w-2xl rounded-[28px] border border-[#001935]/10 bg-white p-0 shadow-[0_30px_90px_rgba(0,25,53,0.2)]" data-testid="agent-network-post-deal-dialog">
          <form className="grid gap-0" data-testid="agent-network-post-deal-form" onSubmit={(event) => { event.preventDefault(); onPostDeal(() => setShowDealModal(false)); }}>
            <DialogHeader className="border-b border-[#001935]/8 px-6 py-6">
              <DialogTitle className="text-3xl font-semibold text-[#001935]">Post a B2B lead</DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-7 text-[#42566f]">
                Keep the brief anonymous. Share the property type, transaction type, district, budget, and referral split only.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 px-6 py-6">
              <div className="grid gap-4 md:grid-cols-2">
                <select className={fieldClass} data-testid="agent-network-deal-type-select" onChange={(event) => onChangeDealField("lead_type", event.target.value)} value={dealForm.lead_type}>
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Industrial">Industrial</option>
                </select>
                <select className={fieldClass} data-testid="agent-network-deal-transaction-select" onChange={(event) => onChangeDealField("transaction_type", event.target.value)} value={dealForm.transaction_type}>
                  <option value="Buy">Buy</option>
                  <option value="Rent">Rent</option>
                  <option value="Sell">Sell</option>
                </select>
                <input className={fieldClass} data-testid="agent-network-deal-budget-input" onChange={(event) => onChangeDealField("budget", event.target.value)} placeholder="Budget e.g. $5,000,000" value={dealForm.budget} />
                <input className={fieldClass} data-testid="agent-network-deal-district-input" onChange={(event) => onChangeDealField("location_district", event.target.value)} placeholder="District e.g. 22" value={dealForm.location_district} />
              </div>
              <input className={fieldClass} data-testid="agent-network-deal-fee-input" onChange={(event) => onChangeDealField("referral_fee_percentage", event.target.value)} placeholder="Referral fee e.g. 20" value={dealForm.referral_fee_percentage} />
              <textarea className="min-h-[150px] rounded-[20px] border border-[#001935]/12 bg-white px-4 py-4 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-network-deal-description-input" onChange={(event) => onChangeDealField("description", event.target.value)} placeholder="Describe the requirement without client names or identifying personal details." value={dealForm.description} />
            </div>

            <DialogFooter className="border-t border-[#001935]/8 px-6 py-5">
              <button className="outline-button" data-testid="agent-network-deal-cancel-button" onClick={() => setShowDealModal(false)} type="button">Cancel</button>
              <button className="gold-button" data-testid="agent-network-deal-submit-button" type="submit">
                {postingDeal ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />} Post Lead
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};